import { cloneDeep } from "lodash";
import {
    allBudgetTypesOptions,
    allClassificationTypesOptions,
    allDataSourcesOptions,
    allMeasureUnitsOptions,
    allPeriodicitiesOptions,
    allReportNamesOptions,
    allReportTypesOptions,
    allStatusOptions,
} from "../../common/baseOptions";
import { Template } from "../../common/types/Template";
import { SvodMbFilterSettings } from "./settings/SvodMbFilterSettings";
import { SvodMbFilterOptions } from "./options/SvodMbFilterOptions";
import { SvodMbIndicator, SvodMbIndicatorsState } from "./types/SvodMbIndicators";
import { SvodMbIncomeValue, SvodMbExpenseValue } from "./table-data/SvodMbTableData";

/**
 * Описание одной колонки таблицы SVOD-MB (§3.1/§4.1 uc-01-report.md).
 *
 * key - показатель для выпадающего списка "Скрыть показатели" (RQ.08) и одновременно ключ
 * колонки в разметке таблицы. Один ключ = одна колонка (эталон - report-cash-exec.ts:27-45),
 * а НЕ пять обобщенных ключей, как было в каркасе EAK-1148.
 *
 * kind определяет, откуда берется значение ячейки:
 *  - 'raw'     - значение поля valueField с бэкенда как есть (без пересчета);
 *  - 'percent' - клиентский расчет % исполнения = factField / planField * 100 (RQ.21);
 *  - 'deviation' - клиентский расчет отклонения = planField - factField (RQ.21);
 *  - 'sum'     - клиентский расчет суммы нескольких полей бэкенда (используется только для
 *                "Свободные остатки на КСН -> всего" = ksn_region + ksn_msu).
 */
export interface SvodMbColumnDef {
    key: string
    kind: 'raw' | 'percent' | 'deviation' | 'sum'
    valueField?: keyof SvodMbIncomeValue | keyof SvodMbExpenseValue
    planField?: keyof SvodMbIncomeValue | keyof SvodMbExpenseValue
    factField?: keyof SvodMbIncomeValue | keyof SvodMbExpenseValue
    sumFields?: (keyof SvodMbIncomeValue | keyof SvodMbExpenseValue)[]
    /** Ключ подписи колонки: modules.budget_execution_monitoring.reports_constructor.svod_mb.table.columns.<labelKey> */
    labelKey: string
}

/** Блок таблицы - группа колонок с общим объединенным заголовком (§3.1/§4.1). */
export interface SvodMbBlockDef {
    key: string
    /** Ключ подписи блока: modules.budget_execution_monitoring.reports_constructor.svod_mb.table.blocks.<labelKey> */
    labelKey: string
    /** Одноколоночный блок: заголовок занимает обе строки шапки (rowspan=2) */
    mergeHeader?: boolean
    columns: SvodMbColumnDef[]
}

/** Стандартный блок из 5 колонок: скорр.план / план на дату / факт / % / отклонение (RQ.21). */
function fiveColumnBlock(prefix: string, blockLabelKey: string, deviationLabelKey: string): SvodMbBlockDef {
    return {
        key: prefix,
        labelKey: blockLabelKey,
        columns: [
            { key: `${prefix}_plan`, kind: 'raw', valueField: `${prefix}_plan` as any, labelKey: 'plan' },
            { key: `${prefix}_plan_period`, kind: 'raw', valueField: `${prefix}_plan_period` as any, labelKey: 'plan_period' },
            { key: `${prefix}_fact`, kind: 'raw', valueField: `${prefix}_fact` as any, labelKey: 'fact' },
            {
                key: `${prefix}_percent`,
                kind: 'percent',
                planField: `${prefix}_plan_period` as any,
                factField: `${prefix}_fact` as any,
                labelKey: 'percent',
            },
            {
                key: `${prefix}_deviation`,
                kind: 'deviation',
                planField: `${prefix}_plan_period` as any,
                factField: `${prefix}_fact` as any,
                labelKey: deviationLabelKey,
            },
        ],
    }
}

/** Блоки доходной части (§3.1): ВСЕГО ПОСТУПЛЕНИЯ / СОБСТВЕННЫЕ ДОХОДЫ / ЦТ ИЗ РБ,ОБ+БК / ПРОЧИЕ (+ГЦБ) / остатки / КСН. */
export const svodMbIncomeBlocks: SvodMbBlockDef[] = [
    fiveColumnBlock('total', 'total_incomes', 'deviation_income'),
    fiveColumnBlock('own', 'own_incomes', 'deviation_income'),
    fiveColumnBlock('ct', 'ct_incomes', 'deviation_income'),
    fiveColumnBlock('other', 'other_incomes', 'deviation_income'),
    {
        key: 'rest',
        labelKey: 'rest_incomes',
        columns: [
            { key: 'rest_plan', kind: 'raw', valueField: 'rest_plan', labelKey: 'rest_plan' },
            { key: 'rest_plan_period', kind: 'raw', valueField: 'rest_plan_period', labelKey: 'plan_period' },
            { key: 'rest_used', kind: 'raw', valueField: 'rest_used', labelKey: 'fact' },
        ],
    },
    {
        key: 'rest_balancing',
        labelKey: 'rest_balancing_incomes',
        mergeHeader: true,
        columns: [
            { key: 'rest_balancing', kind: 'raw', valueField: 'rest_balancing', labelKey: 'rest_balancing' },
        ],
    },
    {
        key: 'ksn',
        labelKey: 'ksn_incomes',
        columns: [
            { key: 'ksn_total', kind: 'sum', sumFields: ['ksn_region', 'ksn_msu'], labelKey: 'ksn_total' },
            { key: 'ksn_region', kind: 'raw', valueField: 'ksn_region', labelKey: 'ksn_region' },
            { key: 'ksn_msu', kind: 'raw', valueField: 'ksn_msu', labelKey: 'ksn_msu' },
        ],
    },
]

/** Блоки расходной части (§4.1, три блока "в том числе"): ВСЕГО / ЦТ и КРЕДИТЫ ИЗ РБ / ИЗ ОБ / МЕСТНЫЙ БЮДЖЕТ. */
export const svodMbExpenseBlocks: SvodMbBlockDef[] = [
    fiveColumnBlock('total', 'total_expenses', 'deviation_expense_total'),
    fiveColumnBlock('rb', 'rb_expenses', 'deviation_expense'),
    fiveColumnBlock('ob', 'ob_expenses', 'deviation_expense'),
    fiveColumnBlock('mb', 'mb_expenses', 'deviation_expense'),
]

export const allSvodMbIncomeIndicatorKeys: string[] = svodMbIncomeBlocks.flatMap(b => b.columns.map(c => c.key))
export const allSvodMbExpenseIndicatorKeys: string[] = svodMbExpenseBlocks.flatMap(b => b.columns.map(c => c.key))

function buildIndicators(keys: string[]): SvodMbIndicator[] {
    return keys.map(key => ({ key, active: true }))
}

export function getDefaultSvodMbIndicators(): SvodMbIndicatorsState {
    return {
        incomes: buildIndicators(allSvodMbIncomeIndicatorKeys),
        expenses: buildIndicators(allSvodMbExpenseIndicatorKeys),
    }
}

/** Последний день предыдущего месяца (значение по умолчанию для параметра "Дата") */
export function getSvodMbDefaultDate(): Date {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 0)
}

const tempSettings: SvodMbFilterSettings = {
    reportName: 'SVOD-MB',
    reportType: 'REGULATED',
    classificationType: 'MIXED',
    dataSource: 'IISK',
    periodicity: 'DAY',
    date: null,
    region: null,
    budgetTypes: [2, 3, 6],
    statuses: ['39'],
    measureUnit: 'THOUSAND',
    roundUp: true,
    roundUpTo: 1,
    sortByRegionCode: false,
    indicators: getDefaultSvodMbIndicators(),
}

export const svodMbBaseTemplate: Template = {
    id: null,
    name: 'Данные об исполнении местного бюджета',
    excelName: null,
    description: null,
    category: '50',
    regions: [],
    userId: '',
    filterSettings: tempSettings,
}

export const svodMbFilterOptions: SvodMbFilterOptions = {
    key: 'SVOD-MB',
    reportNames: cloneDeep(allReportNamesOptions),
    reportTypes: cloneDeep(allReportTypesOptions),
    classificationTypes: cloneDeep(allClassificationTypesOptions),
    dataSources: cloneDeep(allDataSourcesOptions),
    periodicities: cloneDeep(allPeriodicitiesOptions),
    regions: [],
    budgetTypes: cloneDeep(allBudgetTypesOptions).filter(it => it.value !== 1),
    statuses: cloneDeep(allStatusOptions),
    measureUnits: cloneDeep(allMeasureUnitsOptions),
}
